/**
 * Passenger / rider name validation.
 *
 * A name must be letters (any script — Telugu, Latin, …) plus ordinary name
 * punctuation (space, dot, apostrophe, hyphen). No digits. This blocks the most
 * common bad input by far: people typing their phone number into the name box
 * on the step right after OTP entry.
 */
// \p{M} = combining marks — Telugu / Devanagari vowel signs and viramas are
// marks, not letters, so an Indic name fails without them.
const NAME_ALLOWED = /^[\p{L}\p{M}\s.'-]+$/u;
const HAS_LETTER = /\p{L}/u;

export const NAME_MIN = 2;
export const NAME_MAX = 40;

/** Trim + collapse internal whitespace. Does NOT validate. */
export const cleanName = (raw) => String(raw || '').trim().replace(/\s{2,}/g, ' ');

/** True when `raw` is an acceptable name. */
export const isValidName = (raw) => {
  const n = cleanName(raw);
  return n.length >= NAME_MIN
    && n.length <= NAME_MAX
    && NAME_ALLOWED.test(n)
    && HAS_LETTER.test(n);
};

/**
 * For server-fed / historic values: return a usable name or '' if it's junk
 * (all digits, symbols, empty). Callers fall back to a generic label.
 */
export const sanitizeName = (raw) => {
  const n = cleanName(raw).slice(0, NAME_MAX);
  return isValidName(n) ? n : '';
};

// Placeholders the server / older builds stamp when there was no real name.
const GENERIC_NAMES = new Set(['customer', 'passenger', 'rider', 'anna', 'guest', 'user']);
export const isGenericName = (raw) => GENERIC_NAMES.has(cleanName(raw).toLowerCase());

/**
 * A value fit to SHOW as someone's name / to heal a profile from: a real name
 * that isn't a generic placeholder. '' otherwise (→ ask for the real name).
 */
export const asDisplayName = (raw) => {
  const n = sanitizeName(raw);
  return n && !isGenericName(n) ? n : '';
};
